<template>
  <aside
    v-if="step"
    class="tph-card"
    role="region"
    :aria-label="`Page tour: ${step.title}`"
    :aria-labelledby="titleId"
  >
    <div class="tph-inner">
      <p class="tph-progress" aria-live="polite">
        {{ stepIndex + 1 }} of {{ stepTotal }}
      </p>
      <h2 :id="titleId" class="tph-title">{{ step.title }}</h2>
      <p class="tph-body">{{ step.body }}</p>
      <div class="tph-actions">
        <button
          type="button"
          class="tph-btn tph-btn--ghost"
          :disabled="stepIndex === 0"
          @click="onBack"
        >
          Back
        </button>
        <button type="button" class="tph-btn tph-btn--primary" @click="onNext">
          {{ isLast ? 'Done' : 'Next' }}
        </button>
        <button type="button" class="tph-btn tph-btn--ghost" @click="onExit">
          Exit tour
        </button>
      </div>
    </div>
  </aside>
</template>

<script>
import { computed, onBeforeUnmount } from 'vue';
import { useTutorialProgress } from '@/composables/useTutorialProgress';

export default {
  name: 'TutorialPageHighlights',
  setup() {
    const tutorial = useTutorialProgress();
    const titleId = 'tutorial-page-highlight-title';

    const step = computed(() => tutorial.getActivePageTourStep());
    const stepIndex = computed(() => tutorial.state.activePageTourStep || 0);
    const stepTotal = computed(() => (tutorial.state.activePageTourSteps || []).length);
    const isLast = computed(() => stepIndex.value >= stepTotal.value - 1);

    function onBack() {
      tutorial.pageTourBack();
    }

    function onNext() {
      tutorial.pageTourNext();
    }

    function onExit() {
      tutorial.exitPageTour();
    }

    onBeforeUnmount(() => {
      // Host unmount safety: clear outline classes
      if (tutorial.state.activePageTourId) {
        tutorial.exitPageTour({ silent: true });
      }
    });

    return {
      step,
      stepIndex,
      stepTotal,
      isLast,
      titleId,
      onBack,
      onNext,
      onExit,
    };
  },
};
</script>

<style scoped>
.tph-card {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: calc(72px + env(safe-area-inset-bottom, 0px));
  z-index: 3150;
  display: flex;
  justify-content: center;
  pointer-events: none;
  text-align: left;
  font-family: 'Titillium Web', sans-serif;
}

.tph-inner {
  pointer-events: auto;
  width: 100%;
  max-width: 420px;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 14px;
  padding: 16px 16px 14px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.35);
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-sizing: border-box;
}

.tph-progress {
  margin: 0 0 6px;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #93c5fd;
}

.tph-title {
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-weight: 900;
  color: #ffffff;
}

.tph-body {
  margin: 0 0 14px;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #cbd5e1;
}

.tph-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tph-btn {
  min-height: 44px;
  padding: 8px 14px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
}

.tph-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.tph-btn--primary {
  background: #0b7ee3;
  color: #fff;
  border: none;
}

.tph-btn--ghost {
  background: transparent;
  color: #e2e8f0;
  border: 1px solid rgba(226, 232, 240, 0.35);
}

.tph-btn:focus-visible {
  outline: 3px solid rgba(96, 165, 250, 0.55);
  outline-offset: 2px;
}

@media (min-width: 914px) {
  .tph-card {
    bottom: 24px;
    left: auto;
    right: 24px;
    justify-content: flex-end;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tph-card,
  .tph-inner {
    transition: none;
  }
}
</style>

<!-- Unscoped: highlight class is applied to page DOM outside this component -->
<style>
.bst-page-tour-target {
  outline: 3px solid rgba(11, 126, 227, 0.85) !important;
  outline-offset: 4px;
  border-radius: 12px;
  position: relative;
  z-index: 2;
  box-shadow: 0 0 0 6px rgba(11, 126, 227, 0.18);
  scroll-margin-top: 96px;
  scroll-margin-bottom: 160px;
}

@media (prefers-reduced-motion: reduce) {
  .bst-page-tour-target {
    transition: none !important;
  }
}
</style>
